import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import { CloudUpload, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { API_URL } from '../utils/config';
import {useToast} from '../components/ToastProvider/ToastProvider';

const OrderFileUpload = ({ orderNumber }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadResults, setUploadResults] = useState([]);
  const { showToast } = useToast();

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file sizes
    const invalidFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (invalidFiles.length > 0) {
      showToast(`${invalidFiles.length} file(s) exceed 10MB limit`, 'error');
      return;
    }

    setSelectedFiles(files);
    setUploadResults([]);
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) {
      showToast('Please select at least one file', 'error');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const formData = new FormData();
      // Append all files - backend expects 'files' as array
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch(`${API_URL}/orders/upload/${orderNumber}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const result = await response.json();
      setProgress(100);

      // Process results
      const results = [];
      result.uploaded_files?.forEach(file => {
        results.push({ file_name: file.file_name, success: true });
      });
      result.failed_files?.forEach(file => {
        results.push({ file_name: file.file_name, success: false, error: file.error });
      });

      setUploadResults(results);

      // Show summary toast
      const successCount = result.successful_uploads || 0;
      const failCount = result.failed_uploads || 0;
      
      if (failCount === 0) {
        showToast(`${successCount} file(s) uploaded successfully`, 'success');
      } else {
        showToast(`${successCount} succeeded, ${failCount} failed`, 'warning');
      }

      // Clear selected files after successful upload
      if (failCount === 0) {
        setSelectedFiles([]);
        // Reset file input
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';
      }

    } catch (error) {
      showToast(`Upload failed: ${error.message}`, 'error');
      setUploadResults([]);
    } finally {
      setUploading(false);
      setTimeout(() => {
        setProgress(0);
      }, 2000);
    }
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setUploadResults([]);
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'center' }}>
          Upload Order Documents
        </Typography>
        
        <input
          accept="*/*"
          style={{ display: 'none' }}
          id="file-upload"
          type="file"
          multiple
          onChange={handleFileSelect}
          disabled={uploading}
        />
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2, flexWrap: 'wrap' }}>
          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUpload />}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Choose Files'}
            </Button>
          </label>

          {selectedFiles.length > 0 && !uploading && (
            <Button
              variant="outlined"
              onClick={uploadFiles}
              disabled={uploading}
            >
              Upload {selectedFiles.length} File(s)
            </Button>
          )}

          {selectedFiles.length > 0 && !uploading && (
            <Button
              variant="text"
              onClick={clearFiles}
              disabled={uploading}
            >
              Clear
            </Button>
          )}
        </Box>

        {/* Selected files list */}
        {selectedFiles.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Files ({selectedFiles.length}):
            </Typography>
            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1 }}>
              {selectedFiles.map((file, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary={file.name}
                    secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Upload results */}
        {uploadResults.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Upload Results:
            </Typography>
            <List dense sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1 }}>
              {uploadResults.map((result, index) => (
                <ListItem key={index} sx={{ py: 0.5 }}>
                  <ListItemText primary={result.file_name} />
                  {result.success ? (
                    <Chip 
                      icon={<CheckCircle />} 
                      label="Success" 
                      color="success" 
                      size="small"
                    />
                  ) : (
                    <Chip 
                      icon={<ErrorIcon />} 
                      label="Failed" 
                      color="error" 
                      size="small"
                      title={result.error}
                    />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {uploading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
              Uploading... {Math.round(progress)}%
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Supported: Any file type (Max 10MB per file)
          {selectedFiles.length > 0 && ` • ${selectedFiles.length} file(s) selected`}
        </Typography>
      </Paper>
    </Box>
  );
};

export default OrderFileUpload;
